import axios, { AxiosInstance } from 'axios'

// Utility function để lấy API Base URL từ .env.local
export const getApiBaseUrl = (): string => {
  const apiUrl = import.meta.env.VITE_API_URL
  if (!apiUrl) {
    return 'http://localhost:8000/api'
  }
  return apiUrl
}

// Utility function để lấy API Base URL không có /api (dùng cho media URLs)
export const getApiBaseUrlWithoutApi = (): string => {
  const apiUrl = getApiBaseUrl()
  return apiUrl.replace('/api', '')
}

// Base URL cho API backend - chỉ lấy từ .env.local
const API_BASE_URL = getApiBaseUrl()

// Tạo axios instance với cấu hình mặc định
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Để gửi cookies/session
})

// Request interceptor để thêm token hoặc CSRF token
apiClient.interceptors.request.use(
  (config) => {
    // Backend dùng session-based auth với cookies
    // Đảm bảo withCredentials luôn được set
    config.withCredentials = true

    // Lấy CSRF token từ cookies nếu có
    if (typeof window !== 'undefined') {
      const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1]

      // Thêm CSRF token cho tất cả requests (kể cả GET) nếu có
      // Một số backend Django yêu cầu CSRF token cho cả GET requests khi dùng session auth
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken
      }

      // Thêm header để backend biết request đến từ frontend
      config.headers['X-Requested-From'] = 'frontend'
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor để xử lý lỗi
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Xử lý lỗi network
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      // Không redirect nếu là network error, để user có thể thấy lỗi
    }

    if (error.response?.status === 401 || error.response?.status === 403) {
      // Unauthorized hoặc Forbidden - có thể do session hết hạn hoặc chưa đăng nhập
      // QUAN TRỌNG: KHÔNG BAO GIỜ clear localStorage ở đây
      // Để AuthContext tự quyết định có giữ user hay không
      // KHÔNG clear localStorage ở đây - để AuthContext quyết định
    }
    return Promise.reject(error)
  }
)

// ==================== AUTHENTICATION APIs ====================

export const authAPI = {
  // Đăng ký tài khoản
  register: async (data: {
    email: string
    password: string
    password_confirm: string
    name: string
    username?: string
  }) => {
    const response = await apiClient.post('/auth/users/register/', data)
    return response.data
  },

  // Đăng nhập
  login: async (data: { email: string; password: string }) => {
    const response = await apiClient.post('/auth/users/login/', data)
    return response.data
  },

  // Đăng xuất
  logout: async () => {
    const response = await apiClient.post('/auth/users/logout/')
    return response.data
  },

  // Lấy thông tin user hiện tại
  getMe: async () => {
    const response = await apiClient.get('/auth/users/me/')
    return response.data
  },

  // Cập nhật profile
  updateProfile: async (data: Partial<{
    username: string
    email: string
    name: string
    bio: string
    avatar: File | string
  }>) => {
    const formData = new FormData()
    Object.keys(data).forEach((key) => {
      const value = data[key as keyof typeof data]
      if (value !== undefined && value !== null) {
        if (value instanceof File) {
          formData.append(key, value)
        } else {
          formData.append(key, String(value))
        }
      }
    })
    const response = await apiClient.put('/auth/users/update_profile/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  // Đăng ký tập thử (contact form)
  submitContactRegistration: async (data: {
    fullName: string
    age: number
    livingArea: string
    interestedArea: string
    message?: string
  }) => {
    const response = await apiClient.post('/auth/contact-registrations/', {
      full_name: data.fullName,
      age: data.age,
      living_area: data.livingArea,
      interested_area: data.interestedArea,
      message: data.message || '',
    })
    return response.data
  },
}

// ==================== COURSES APIs ====================

export const coursesAPI = {
  // Lấy danh sách khóa học (chỉ các khóa học đã đăng ký)
  getCourses: async (params?: { type?: 'online' | 'offline' }) => {
    const response = await apiClient.get('/courses/courses/', { params })
    return response.data
  },

  // Lấy danh sách tất cả khóa học có sẵn để đăng ký (yêu cầu đăng nhập)
  getAvailableCourses: async (params?: { type?: 'online' | 'offline' }) => {
    const response = await apiClient.get('/courses/courses/available/', { params })
    return response.data
  },

  // Lấy danh sách tất cả khóa học công khai (không cần đăng nhập)
  getPublicCourses: async (params?: { type?: 'online' | 'offline' }) => {
    const response = await apiClient.get('/courses/courses/public/', { params })
    return response.data
  },

  // Lấy chi tiết khóa học
  getCourseDetail: async (id: number) => {
    const response = await apiClient.get(`/courses/courses/${id}/`)
    return response.data
  },

  // Lấy khóa học của tôi
  getMyCourses: async () => {
    const response = await apiClient.get('/courses/courses/my_courses/')
    return response.data
  },

  // Đăng ký khóa học
  enroll: async (courseId: number) => {
    const response = await apiClient.post(`/courses/courses/${courseId}/enroll/`)
    return response.data
  },

  // So sánh tiến độ với học viên khác
  compareProgress: async (courseId: number) => {
    const response = await apiClient.get(`/courses/courses/${courseId}/compare_progress/`)
    return response.data
  },

  // Lấy danh sách đăng ký
  getEnrollments: async () => {
    const response = await apiClient.get('/courses/enrollments/')
    return response.data
  },

  // Lấy chi tiết bài học
  getLessonDetail: async (id: number) => {
    const response = await apiClient.get(`/courses/lessons/${id}/`)
    return response.data
  },

  // Hoàn thành bài học
  completeLesson: async (id: number) => {
    const response = await apiClient.post(`/courses/lessons/${id}/complete/`)
    return response.data
  },

  // Tạo đăng ký mới
  createEnrollment: async (data: { course_id: number; payment_status?: string }) => {
    const response = await apiClient.post('/courses/enrollments/', data)
    return response.data
  },

  // Cập nhật đăng ký
  updateEnrollment: async (id: number, data: Partial<{
    progress: number
    completed_videos: number
    status: string
    payment_status: string
  }>) => {
    const response = await apiClient.patch(`/courses/enrollments/${id}/`, data)
    return response.data
  },

  // ==================== VIDEO THỬ THÁCH APIs ====================

  // Lấy danh sách video thử thách (không cần curriculum nữa)
  getCurriculumLessons: async (params?: { day?: number }) => {
    const response = await apiClient.get('/courses/curriculum-lessons/', { params })
    return response.data
  },

  // Lấy chi tiết bài học
  getCurriculumLessonDetail: async (id: number) => {
    const response = await apiClient.get(`/courses/curriculum-lessons/${id}/`)
    return response.data
  },

  // Hoàn thành thử thách và nhận huy hiệu
  completeChallenge: async (lessonId: number) => {
    const response = await apiClient.post(`/courses/curriculum-lessons/${lessonId}/complete_challenge/`)
    return response.data
  },

  // Submit thử thách (upload hình và ghi chú)
  submitChallenge: async (lessonId: number, data: { image: File; reflection: string; videoUrl?: string }) => {
    const formData = new FormData()
    formData.append('image', data.image)
    formData.append('reflection', data.reflection)
    if (data.videoUrl) {
      formData.append('video_url', data.videoUrl)
    }
    const response = await apiClient.post(`/courses/curriculum-lessons/${lessonId}/submit_challenge/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  // Lấy submission của tôi
  getMySubmission: async (lessonId: number) => {
    const response = await apiClient.get(`/courses/curriculum-lessons/${lessonId}/my_submission/`)
    return response.data
  },

  // Lấy bình luận của khóa học
  getCourseComments: async (courseId: number) => {
    const response = await apiClient.get(`/courses/courses/${courseId}/comments/`)
    return response.data
  },

  // Thêm bình luận cho khóa học
  addCourseComment: async (courseId: number, data: { content: string }) => {
    const response = await apiClient.post(`/courses/courses/${courseId}/add_comment/`, data)
    return response.data
  },

  // Thử thách bổ sung (Extra Challenges)
  getExtraChallenges: async () => {
    const response = await apiClient.get('/courses/extra-challenges/')
    return response.data
  },

  // Submit thử thách bổ sung
  submitExtraChallenge: async (data: { challenge: number; image: File; reflection: string }) => {
    const formData = new FormData()
    formData.append('challenge', String(data.challenge))
    formData.append('image', data.image)
    formData.append('reflection', data.reflection)
    const response = await apiClient.post('/courses/extra-challenge-submissions/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },
}

// ==================== CHECKINS APIs ====================

export const checkinsAPI = {
  // Lấy danh sách điểm danh
  getCheckins: async () => {
    const response = await apiClient.get('/checkins/checkins/')
    return response.data
  },

  // Tạo điểm danh
  createCheckin: async (data?: {
    image?: File | string
    notes?: string
  }) => {
    const formData = new FormData()
    if (data?.image instanceof File) {
      formData.append('image', data.image)
    }
    if (data?.notes) {
      formData.append('notes', data.notes)
    }
    const response = await apiClient.post('/checkins/checkins/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  // Lấy điểm danh hôm nay
  getTodayCheckin: async () => {
    const response = await apiClient.get('/checkins/checkins/today/')
    return response.data
  },

  // Lấy lịch điểm danh
  getCalendar: async (params?: { year?: number; month?: number }) => {
    const response = await apiClient.get('/checkins/checkins/calendar/', { params })
    return response.data
  },

  // Lấy trạng thái điểm danh
  getStatus: async () => {
    const response = await apiClient.get('/checkins/checkins/status/')
    return response.data
  },

  // Lấy danh sách nhật ký tập luyện
  getWorkoutDiaries: async () => {
    const response = await apiClient.get('/checkins/workout-diaries/')
    return response.data
  },

  // Tạo nhật ký tập luyện
  createWorkoutDiary: async (data: {
    description?: string
    duration?: number
    health_before?: number
    health_after?: number
    pain_areas?: string[]
    notes?: string
  }) => {
    const response = await apiClient.post('/checkins/workout-diaries/', data)
    return response.data
  },

  // Lấy nhật ký hôm nay
  getTodayWorkoutDiary: async () => {
    const response = await apiClient.get('/checkins/workout-diaries/today/')
    return response.data
  },

  // Lấy nhật ký theo ngày cụ thể
  getDiaryByDate: async (date: string) => {
    const response = await apiClient.get(`/checkins/workout-diaries/by_date/?date=${date}`)
    return response.data
  },

  // Lấy thống kê số phút tập luyện 7 ngày gần nhất
  getWeeklyStats: async () => {
    const response = await apiClient.get('/checkins/workout-diaries/weekly_stats/')
    return response.data
  },
}

// ==================== LEADERBOARD APIs ====================

export const leaderboardAPI = {
  // Lấy danh sách huy hiệu
  getBadges: async () => {
    const response = await apiClient.get('/leaderboard/badges/')
    return response.data
  },

  // Lấy huy hiệu của tôi
  getMyBadges: async () => {
    const response = await apiClient.get('/leaderboard/badges/my_badges/')
    return response.data
  },

  // Lấy top players
  getTopPlayers: async () => {
    const response = await apiClient.get('/leaderboard/leaderboard/top_players/')
    return response.data
  },

  // Lấy top 10 tháng trước
  getPreviousMonthTop: async () => {
    const response = await apiClient.get('/leaderboard/leaderboard/previous_month_top/')
    return response.data
  },

  // Lấy xếp hạng của tôi
  getMyRanking: async () => {
    const response = await apiClient.get('/leaderboard/leaderboard/my_ranking/')
    return response.data
  },

  // Xem profile của học viên khác
  getProfile: async (userId: number) => {
    const response = await apiClient.get(`/leaderboard/leaderboard/${userId}/profile/`)
    return response.data
  },

  // Lấy tổng số user
  getTotalUsers: async () => {
    const response = await apiClient.get('/leaderboard/leaderboard/total_users/')
    return response.data
  },

  // Lấy danh sách các tháng có lịch sử top 10
  getMonthlyHistoryList: async () => {
    const response = await apiClient.get('/leaderboard/leaderboard/monthly_history_list/')
    return response.data
  },

  // Lấy top 10 của tháng cụ thể (?year=2026&month=2)
  getMonthlyHistory: async (year: number, month: number) => {
    const response = await apiClient.get('/leaderboard/leaderboard/monthly_history/', {
      params: { year, month }
    })
    return response.data
  },
}

// ==================== PAYMENTS APIs ====================

export const paymentsAPI = {
  // Lấy danh sách thanh toán
  getPayments: async () => {
    const response = await apiClient.get('/payments/payments/')
    return response.data
  },

  // Tạo thanh toán (upload ảnh chuyển khoản)
  createPayment: async (data: {
    course_enrollment_id: number
    amount: number
    payment_method: string
    transfer_image: File
    notes?: string
  }) => {
    const formData = new FormData()
    formData.append('course_enrollment', String(data.course_enrollment_id))
    formData.append('amount', String(data.amount))
    formData.append('payment_method', data.payment_method)
    formData.append('transfer_image', data.transfer_image)
    if (data.notes) {
      formData.append('notes', data.notes)
    }
    const response = await apiClient.post('/payments/payments/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  // Lấy chi tiết thanh toán
  getPaymentDetail: async (id: number) => {
    const response = await apiClient.get(`/payments/payments/${id}/`)
    return response.data
  },
}

// ==================== TRAINING GROUPS APIs ====================

export const trainingGroupsAPI = {
  // Lấy danh sách nhóm tập luyện
  getGroups: async () => {
    const response = await apiClient.get('/training-groups/groups/')
    return response.data
  },

  // Lấy chi tiết nhóm
  getGroupDetail: async (id: number) => {
    const response = await apiClient.get(`/training-groups/groups/${id}/`)
    return response.data
  },

  // Lấy danh sách nhóm luyện tập của user hiện tại
  getMyGroups: async () => {
    const response = await apiClient.get('/training-groups/groups/my_groups/')
    return response.data
  },

  // Lấy danh sách thông báo
  getAnnouncements: async () => {
    const response = await apiClient.get('/training-groups/announcements/')
    return response.data
  },

  // Tạo thông báo
  createAnnouncement: async (data: {
    training_group_id?: number
    title: string
    content: string
    priority?: string
    image?: File | string
    is_pinned?: boolean
  }) => {
    const formData = new FormData()
    if (data.training_group_id) {
      formData.append('training_group', String(data.training_group_id))
    }
    formData.append('title', data.title)
    formData.append('content', data.content)
    if (data.priority) {
      formData.append('priority', data.priority)
    }
    if (data.image instanceof File) {
      formData.append('image', data.image)
    }
    if (data.is_pinned !== undefined) {
      formData.append('is_pinned', String(data.is_pinned))
    }
    const response = await apiClient.post('/training-groups/announcements/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  // Lấy chi tiết thông báo
  getAnnouncementDetail: async (id: number) => {
    const response = await apiClient.get(`/training-groups/announcements/${id}/`)
    return response.data
  },

  // Cập nhật thông báo
  updateAnnouncement: async (id: number, data: Partial<{
    title: string
    content: string
    priority: string
    is_pinned: boolean
  }>) => {
    const response = await apiClient.patch(`/training-groups/announcements/${id}/`, data)
    return response.data
  },

  // Xóa thông báo
  deleteAnnouncement: async (id: number) => {
    const response = await apiClient.delete(`/training-groups/announcements/${id}/`)
    return response.data
  },

  // Lấy bình luận của thông báo
  getComments: async (announcementId: number) => {
    const response = await apiClient.get(`/training-groups/announcements/${announcementId}/comments/`)
    return response.data
  },

  // Thêm bình luận
  addComment: async (announcementId: number, data: { content: string }) => {
    const response = await apiClient.post(`/training-groups/announcements/${announcementId}/add_comment/`, data)
    return response.data
  },

  // Lấy danh sách bình luận của nhóm luyện tập
  getGroupComments: async (groupId: number) => {
    const response = await apiClient.get(`/training-groups/groups/${groupId}/comments/`)
    return response.data
  },

  // Thêm bình luận vào nhóm luyện tập
  addGroupComment: async (groupId: number, data: { content: string }) => {
    const response = await apiClient.post(`/training-groups/groups/${groupId}/add_comment/`, data)
    return response.data
  },
}

// ==================== REPORTS APIs ====================

export const reportsAPI = {
  // Lấy danh sách báo cáo
  getReports: async () => {
    const response = await apiClient.get('/reports/reports/')
    return response.data
  },

  // Tạo báo cáo
  createReport: async (data: {
    reported_user_id: number
    report_type: string
    title: string
    description: string
    evidence_image?: File
    check_in_id?: number
    check_in_date?: string
  }) => {
    const formData = new FormData()
    formData.append('reported_user', String(data.reported_user_id))
    formData.append('report_type', data.report_type)
    formData.append('title', data.title)
    formData.append('description', data.description)
    if (data.evidence_image instanceof File) {
      formData.append('evidence_image', data.evidence_image)
    }
    if (data.check_in_id) {
      formData.append('check_in', String(data.check_in_id))
    }
    if (data.check_in_date) {
      formData.append('check_in_date', data.check_in_date)
    }
    const response = await apiClient.post('/reports/reports/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  // Lấy chi tiết báo cáo
  getReportDetail: async (id: number) => {
    const response = await apiClient.get(`/reports/reports/${id}/`)
    return response.data
  },

  // Lấy báo cáo mà tôi bị báo cáo
  getMyReportsReceived: async () => {
    const response = await apiClient.get('/reports/reports/my_reports_received/')
    return response.data
  },
}

export const appealsAPI = {
  // Lấy danh sách kháng cáo
  getAppeals: async () => {
    const response = await apiClient.get('/reports/appeals/')
    return response.data
  },

  // Tạo kháng cáo
  createAppeal: async (data: {
    report: number
    reason: string
    evidence_image?: File
  }) => {
    const formData = new FormData()
    formData.append('report', String(data.report))
    formData.append('reason', data.reason)
    if (data.evidence_image instanceof File) {
      formData.append('evidence_image', data.evidence_image)
    }
    const response = await apiClient.post('/reports/appeals/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  // Lấy chi tiết kháng cáo
  getAppealDetail: async (id: number) => {
    const response = await apiClient.get(`/reports/appeals/${id}/`)
    return response.data
  },
}

// ==================== SYSTEM APIs ====================

export const systemAPI = {
  // Lấy theme mode
  getTheme: async () => {
    const response = await apiClient.get('/system/settings/theme/')
    return response.data
  },

  // Cập nhật theme mode
  setTheme: async (themeMode: 'light' | 'dark' | 'auto') => {
    const response = await apiClient.post('/system/settings/theme/', { theme_mode: themeMode })
    return response.data
  },
}

// Export default API client nếu cần sử dụng trực tiếp
export default apiClient

